import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, ArrowRight } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { airports } from "@/data/airports";

interface BlogCrossLinksProps {
  airportCode: string;
  maxItems?: number;
}

/**
 * Contextual cross-links from airport pages to relevant blog posts.
 * Always shows universal travel tips + any airport-specific matches.
 */
export const BlogCrossLinks = ({ airportCode, maxItems = 4 }: BlogCrossLinksProps) => {
  const airport = airports.find(a => a.code === airportCode);
  const cityName = airport?.city.split(",")[0].toLowerCase() || "";

  // Score posts by relevance to this airport
  const scored = blogPosts
    .map(post => {
      let score = 0;
      const haystack = (post.title + " " + post.description + " " + post.keywords.join(" ")).toLowerCase();
      if (haystack.includes(airportCode.toLowerCase())) score += 10;
      if (cityName && haystack.includes(cityName)) score += 8;
      if (haystack.includes("tsa wait")) score += 3;
      if (haystack.includes("security")) score += 2;
      if (haystack.includes("precheck")) score += 2;
      if (post.dateModified) score += 1; // prefer fresh content
      return { post, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxItems);

  if (scored.length === 0) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          {airportCode} Travel Guides & Tips
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {scored.map(({ post }) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors"
            >
              <h4 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 flex items-center gap-1">
                {post.title}
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </h4>
              <p className="text-xs text-muted-foreground mt-1">{post.readTime}</p>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
